/**
 * Schemas Zod e tipos das tabelas relacionadas à entidade Clínica
 * (`clinics`, `clinic_members`, `subscriptions`, `clinic_provisioning_events`).
 *
 * Este módulo cobre **leitura no painel superadmin** — o painel da
 * Karla precisa olhar o estado do tenant após provisionamento. Edge
 * functions (provision-clinic, etc) não dependem destes schemas; cada
 * função tem sua própria validação Zod isolada.
 */
import { z } from 'zod';

/** Schema da tabela `public.clinics` (migration 0003). */
export const clinicSchema = z.object({
  id: z.string().uuid(),
  legal_name: z.string(),
  trade_name: z.string().nullable(),
  cnpj: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.unknown(), // JSONB com estrutura variada
  logo_url: z.string().nullable(),
  theme_color: z.string().nullable(),
  status: z.string(),
  has_consulting: z.boolean(),
  consulting_tier: z.string(),
  consulting_started_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Clinic = z.infer<typeof clinicSchema>;

/** Status canônicos esperados em `clinics.status` — origina de migration
 * 0003; adicionados conforme funil. As labels humanas vivem no namespace
 * i18n `clinics` (regra inviolável §5.3 do AGENTS.md): `clinics:status.<key>`.
 * Quando vier valor desconhecido, o `getClinicStatusStyle` cai no
 * fallback `neutral` e a UI renderiza o status raw (não quebra). */
export const CLINIC_STATUSES = ['active', 'trialing', 'paused', 'suspended', 'cancelled'] as const;
export type ClinicStatus = (typeof CLINIC_STATUSES)[number];

/** Schema da tabela `public.subscriptions`. */
export const subscriptionSchema = z.object({
  id: z.string().uuid(),
  clinic_id: z.string().uuid(),
  plan_id: z.string().uuid(),
  status: z.string(),
  billing_cycle: z.string(),
  asaas_customer_id: z.string().nullable(),
  asaas_subscription_id: z.string().nullable(),
  current_period_start: z.string(),
  current_period_end: z.string(),
  trial_ends_at: z.string().nullable(),
  canceled_at: z.string().nullable(),
  cancel_at_period_end: z.boolean(),
  metadata: z.record(z.unknown()).nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

/** Schema simplificado de `public.plans` (apenas campos exibidos no
 * painel). Usado em joins via embed. */
export const planLiteSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name_pt_br: z.string(),
  tier: z.string(),
  monthly_price_brl: z.number().nullable(),
  yearly_price_brl: z.number().nullable(),
  billing_cycle: z.string(),
});
export type PlanLite = z.infer<typeof planLiteSchema>;

/** Schema da tabela `public.clinic_members`. */
export const clinicMemberSchema = z.object({
  id: z.string().uuid(),
  clinic_id: z.string().uuid(),
  user_id: z.string().uuid(),
  role_key: z.string(),
  status: z.string(),
  invited_by: z.string().uuid().nullable(),
  invited_at: z.string().nullable(),
  accepted_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type ClinicMember = z.infer<typeof clinicMemberSchema>;

/** Schema da tabela `public.clinic_provisioning_events`. */
export const provisioningEventSchema = z.object({
  id: z.string().uuid(),
  contract_id: z.string().uuid().nullable(),
  clinic_id: z.string().uuid().nullable(),
  step: z.string(),
  status: z.enum(['pending', 'success', 'failed']).or(z.string()),
  details: z.record(z.unknown()).nullable().optional(),
  error_message: z.string().nullable(),
  created_at: z.string(),
});
export type ProvisioningEvent = z.infer<typeof provisioningEventSchema>;

/** Steps canônicos do provisionamento — alinhados ao que o
 * `provision-clinic/handler.ts` insere em `clinic_provisioning_events.step`.
 * Labels vivem no namespace i18n `clinics:provisioningSteps.<key>`. */
export const PROVISIONING_STEPS = [
  'clinic_created',
  'owner_member_created',
  'asaas_customer_created',
  'subscription_created',
  'asaas_subscription_created',
  'email_welcome_sent',
  'provisioning_complete',
  'provisioning_failed',
] as const;
export type ProvisioningStep = (typeof PROVISIONING_STEPS)[number];

/** Display name humano da clínica — `trade_name` (se existir) é
 * preferido por ser o que aparece pra paciente; cai pra `legal_name`. */
export function clinicDisplayName(clinic: Clinic): string {
  return clinic.trade_name?.trim() || clinic.legal_name;
}
